/**
 * Cách hiển thị của câu hỏi `select_one`, và cấu hình ô tìm kiếm của danh sách
 * thả xuống.
 *
 * Tách thành module riêng vì hai nơi phải hiểu **giống hệt nhau**: renderer (lúc
 * điền phiếu) và Question Builder (lúc soạn câu hỏi). Lệch nhau một chút là admin
 * chọn "radio" nhưng điều tra viên vẫn thấy danh sách thả xuống.
 *
 * Quy ước lưu trong schema — dùng cột XLSForm sẵn có nên file xuất ra ODK vẫn đúng:
 *
 *  - `appearance` chứa `radio`   → nút radio, mọi lựa chọn hiện sẵn.
 *    (ODK không có appearance `radio`; nó bỏ qua giá trị lạ và rơi về cách hiển thị
 *     mặc định của select_one — vốn chính là danh sách nút radio. Trùng ý đồ.)
 *  - `appearance` chứa `minimal` → danh sách thả xuống, đúng cách ODK hiểu.
 *  - `appearance` rỗng → **danh sách thả xuống**. Khác mặc định của ODK, nhưng đây
 *    là cách mọi phiên bản bộ câu hỏi phát hành trước đây đang hiển thị; R6 buộc
 *    phiếu cũ mở lại phải y nguyên nên không được đổi mặc định này.
 *
 * Ô tìm kiếm nằm ở cột riêng `ui_search` (cùng họ với `ui_display`, `ui_table_*`):
 * `yes` luôn hiện · `no` không bao giờ hiện · rỗng/`auto` thì theo số lựa chọn.
 */

/** Số lựa chọn tối thiểu để chế độ `auto` gắn thêm ô tìm kiếm. */
export const SEARCHABLE_OPTION_THRESHOLD = 12;

const MODE_TOKENS = { radio: 'radio', dropdown: 'minimal' };

function tokensOf(appearance) {
  return String(appearance || '')
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
}

/** @returns {'radio'|'dropdown'} */
export function selectOneMode(row) {
  return tokensOf(row?.appearance).includes('radio') ? 'radio' : 'dropdown';
}

/** Hiện ngang trên màn nhập máy tính khi đủ ít lựa chọn và đủ ngắn để nằm trên một, hai dòng. */
const PAPER_RADIO_MAX_OPTIONS = 5;
const PAPER_RADIO_MAX_LABEL = 40;

/**
 * Màn nhập trên máy tính giống phiếu giấy: lựa chọn ngắn hiện ngang (☐ Có ☐ Không), Doc/11a.
 * Chỉ đổi cách hiện, giá trị lưu vẫn là một mã nên phiếu cũ mở ra không sai lệch gì. `minimal` là
 * người soạn phiếu đã chọn ô thả xuống; `appearance` rỗng mới là chỗ được quyền đổi.
 *
 * @returns {'radio'|'dropdown'}
 */
export function paperSelectMode(row, options) {
  const tokens = tokensOf(row?.appearance);
  if (tokens.includes('radio')) return 'radio';
  if (tokens.includes('minimal')) return 'dropdown';
  const short =
    options.length <= PAPER_RADIO_MAX_OPTIONS &&
    options.every((o) => String(o.label ?? '').length <= PAPER_RADIO_MAX_LABEL);
  return short ? 'radio' : 'dropdown';
}

/**
 * Đặt lại cách hiển thị trong chuỗi `appearance`, giữ nguyên mọi token khác
 * (`compact`, `likert`… có thể do người khác thêm vào).
 * @param {string} appearance
 * @param {'radio'|'dropdown'|null} mode - `null` để gỡ hẳn (câu hỏi không còn là select_one).
 */
export function withSelectOneMode(appearance, mode) {
  const kept = tokensOf(appearance).filter((t) => t !== 'radio' && t !== 'minimal');
  const token = mode ? MODE_TOKENS[mode] : null;
  return [...kept, ...(token ? [token] : [])].join(' ');
}

/** @returns {'auto'|'yes'|'no'} */
export function optionSearchMode(row) {
  const raw = String(row?.ui_search || '').trim().toLowerCase();
  return raw === 'yes' || raw === 'no' ? raw : 'auto';
}

/** Có gắn ô tìm kiếm phía trên danh sách thả xuống này không. */
export function shouldShowOptionSearch(row, optionCount) {
  const mode = optionSearchMode(row);
  if (mode === 'yes') return true;
  if (mode === 'no') return false;
  return optionCount >= SEARCHABLE_OPTION_THRESHOLD;
}
